/**
 * Biodiversity Scoring Service
 * 
 * Implements scientific biodiversity indices as per PRD FR-002:
 * - Shannon Diversity Index (H')
 * - Simpson Diversity Index (D)
 * - Composite Biodiversity Score
 * - Monoculture Risk Assessment
 * 
 * Reference: PRD Section 5.1, Feature 2
 */

/**
 * Calculate Shannon Diversity Index (H')
 * H' = -Σ(pi * ln(pi))
 * Where pi is the proportion of species i
 * 
 * @param {number[]} speciesCounts - Array of species counts/abundances
 * @returns {number} Shannon Diversity Index value
 */
export const calculateShannonIndex = (speciesCounts) => {
  if (!speciesCounts || speciesCounts.length === 0) return 0;

  const total = speciesCounts.reduce((sum, count) => sum + count, 0);
  if (total === 0) return 0;

  let shannonIndex = 0;

  for (const count of speciesCounts) {
    if (count > 0) {
      const proportion = count / total;
      shannonIndex -= proportion * Math.log(proportion);
    }
  }

  return shannonIndex;
};

/**
 * Calculate Simpson Diversity Index (D)
 * D = 1 - Σ(pi²)
 * Higher values indicate greater diversity
 * 
 * @param {number[]} speciesCounts - Array of species counts/abundances
 * @returns {number} Simpson Diversity Index (0-1)
 */
export const calculateSimpsonIndex = (speciesCounts) => {
  if (!speciesCounts || speciesCounts.length === 0) return 0;

  const total = speciesCounts.reduce((sum, count) => sum + count, 0);
  if (total === 0) return 0;

  let sumSquaredProportions = 0;

  for (const count of speciesCounts) {
    const proportion = count / total;
    sumSquaredProportions += proportion * proportion;
  }

  // Simpson's Index of Diversity (1 - D)
  return 1 - sumSquaredProportions;
};

/**
 * Calculate Species Richness (simple count)
 * 
 * @param {number[]} speciesCounts - Array of species counts
 * @returns {number} Number of species present
 */
export const calculateSpeciesRichness = (speciesCounts) => {
  if (!speciesCounts) return 0;
  return speciesCounts.filter(count => count > 0).length;
};

/**
 * Calculate Evenness (Pielou's J)
 * J = H' / ln(S)
 * Where S is species richness
 * 
 * @param {number[]} speciesCounts - Array of species counts
 * @returns {number} Evenness value (0-1)
 */
export const calculateEvenness = (speciesCounts) => {
  const richness = calculateSpeciesRichness(speciesCounts);
  if (richness <= 1) return 1;

  const shannon = calculateShannonIndex(speciesCounts);
  return shannon / Math.log(richness);
};

/**
 * Monoculture Risk Levels based on PRD specifications
 */
export const MONOCULTURE_RISK_LEVELS = {
  CRITICAL: { level: 'Critical', threshold: 85, color: '#dc2626', description: '>85% single crop concentration' },
  HIGH: { level: 'High', threshold: 70, color: '#ea580c', description: '70-85% single crop concentration' },
  MODERATE: { level: 'Moderate', threshold: 50, color: '#ca8a04', description: '50-70% single crop concentration' },
  LOW: { level: 'Low', threshold: 30, color: '#16a34a', description: '30-50% single crop concentration' },
  MINIMAL: { level: 'Minimal', threshold: 0, color: '#059669', description: '<30% single crop concentration' }
};

/**
 * Calculate Monoculture Risk based on crop concentration
 * 
 * @param {Object[]} cropData - Array of {crop: string, area: number}
 * @returns {Object} Risk assessment with level, percentage, and recommendations
 */
export const calculateMonocultureRisk = (cropData) => {
  if (!cropData || cropData.length === 0) {
    return {
      riskLevel: MONOCULTURE_RISK_LEVELS.MINIMAL,
      dominantCrop: null,
      concentration: 0,
      recommendations: ['Add crop data to assess monoculture risk']
    };
  }

  const totalArea = cropData.reduce((sum, crop) => sum + crop.area, 0);
  if (totalArea === 0) {
    return {
      riskLevel: MONOCULTURE_RISK_LEVELS.MINIMAL,
      dominantCrop: null,
      concentration: 0,
      recommendations: []
    };
  }

  // Find dominant crop
  const sortedCrops = [...cropData].sort((a, b) => b.area - a.area);
  const dominantCrop = sortedCrops[0];
  const concentration = (dominantCrop.area / totalArea) * 100;

  // Determine risk level
  let riskLevel;
  if (concentration >= 85) {
    riskLevel = MONOCULTURE_RISK_LEVELS.CRITICAL;
  } else if (concentration >= 70) {
    riskLevel = MONOCULTURE_RISK_LEVELS.HIGH;
  } else if (concentration >= 50) {
    riskLevel = MONOCULTURE_RISK_LEVELS.MODERATE;
  } else if (concentration >= 30) {
    riskLevel = MONOCULTURE_RISK_LEVELS.LOW;
  } else {
    riskLevel = MONOCULTURE_RISK_LEVELS.MINIMAL;
  }

  // Generate recommendations based on risk
  const recommendations = generateMonocultureRecommendations(riskLevel, dominantCrop.crop, concentration, cropData);

  return {
    riskLevel,
    dominantCrop: dominantCrop.crop,
    concentration: Math.round(concentration * 10) / 10,
    cropDistribution: cropData.map(c => ({
      ...c,
      percentage: Math.round((c.area / totalArea) * 1000) / 10
    })),
    recommendations
  };
};

/**
 * Generate recommendations based on monoculture risk
 */
const generateMonocultureRecommendations = (riskLevel, dominantCrop, concentration, cropData) => {
  const recommendations = [];

  if (riskLevel.level === 'Critical' || riskLevel.level === 'High') {
    recommendations.push(
      `Urgent: Reduce ${dominantCrop} concentration from ${concentration.toFixed(1)}% to below 50%`,
      'Implement immediate crop diversification program',
      'Consider cover cropping on 20% of dominant crop area',
      'Establish pollinator habitat zones (minimum 5% of total area)'
    );
  } else if (riskLevel.level === 'Moderate') {
    recommendations.push(
      `Plan gradual reduction of ${dominantCrop} over next 2 seasons`,
      'Introduce 2-3 complementary crop species',
      'Implement crop rotation schedule',
      'Consider intercropping opportunities'
    );
  } else if (riskLevel.level === 'Low') {
    recommendations.push(
      'Maintain current crop diversity',
      'Monitor for any concentration increases',
      'Consider adding specialty crops for market diversity',
      'Implement biodiversity buffer strips'
    );
  } else {
    recommendations.push(
      'Excellent crop diversity - maintain current practices',
      'Document successful diversification strategies',
      'Consider adding native species habitat areas',
      'Share best practices with neighboring regions'
    );
  }

  return recommendations;
};

/**
 * Biodiversity Score Rating Levels (per PRD)
 */
export const BIODIVERSITY_RATINGS = {
  EXCEPTIONAL: { rating: 'Exceptional', min: 90, color: '#059669' },
  EXCELLENT: { rating: 'Excellent', min: 80, color: '#16a34a' },
  GOOD: { rating: 'Good', min: 70, color: '#65a30d' },
  FAIR: { rating: 'Fair', min: 60, color: '#ca8a04' },
  NEEDS_IMPROVEMENT: { rating: 'Needs Improvement', min: 40, color: '#ea580c' },
  POOR: { rating: 'Poor', min: 0, color: '#dc2626' }
};

/**
 * Get biodiversity rating based on score
 * 
 * @param {number} score - Biodiversity score (0-100)
 * @returns {Object} Rating object with label and color
 */
export const getBiodiversityRating = (score) => {
  if (score >= 90) return BIODIVERSITY_RATINGS.EXCEPTIONAL;
  if (score >= 80) return BIODIVERSITY_RATINGS.EXCELLENT;
  if (score >= 70) return BIODIVERSITY_RATINGS.GOOD;
  if (score >= 60) return BIODIVERSITY_RATINGS.FAIR;
  if (score >= 40) return BIODIVERSITY_RATINGS.NEEDS_IMPROVEMENT;
  return BIODIVERSITY_RATINGS.POOR;
};

/**
 * Calculate Composite Biodiversity Score (0-100)
 * Weighted as per PRD:
 * - Species Diversity: 40%
 * - Habitat Quality: 30%
 * - Farming Practices: 20%
 * - Conservation Efforts: 10%
 * 
 * @param {Object} data - Biodiversity data
 * @returns {Object} Comprehensive biodiversity assessment
 */
export const calculateCompositeBiodiversityScore = (data) => {
  const {
    speciesCounts = [],
    habitatQualityScore = 50,
    farmingPracticesScore = 50,
    conservationScore = 50,
    cropData = []
  } = data;

  // Calculate diversity indices
  const shannonIndex = calculateShannonIndex(speciesCounts);
  const simpsonIndex = calculateSimpsonIndex(speciesCounts);
  const speciesRichness = calculateSpeciesRichness(speciesCounts);
  const evenness = calculateEvenness(speciesCounts);

  // Normalize Shannon Index to 0-100 scale (typical max ~4.5 for natural ecosystems)
  const normalizedShannon = Math.min(100, (shannonIndex / 4.5) * 100);

  // Combine indices for species diversity score (50% Shannon, 50% Simpson)
  const speciesDiversityScore = (normalizedShannon * 0.5) + (simpsonIndex * 100 * 0.5);

  // Calculate weighted composite score
  const compositeScore = (
    speciesDiversityScore * 0.40 +
    habitatQualityScore * 0.30 +
    farmingPracticesScore * 0.20 +
    conservationScore * 0.10
  );

  // Get monoculture risk
  const monocultureRisk = calculateMonocultureRisk(cropData);

  // Get rating
  const rating = getBiodiversityRating(compositeScore);

  // Generate actionable recommendations
  const recommendations = generateBiodiversityRecommendations({
    compositeScore,
    speciesDiversityScore,
    habitatQualityScore,
    farmingPracticesScore,
    conservationScore,
    monocultureRisk
  });

  return {
    // Overall score
    compositeScore: Math.round(compositeScore * 10) / 10,
    rating,

    // Individual indices
    indices: {
      shannonIndex: Math.round(shannonIndex * 1000) / 1000,
      simpsonIndex: Math.round(simpsonIndex * 1000) / 1000,
      speciesRichness,
      evenness: Math.round(evenness * 1000) / 1000
    },

    // Component scores
    components: {
      speciesDiversity: { score: Math.round(speciesDiversityScore * 10) / 10, weight: 40 },
      habitatQuality: { score: habitatQualityScore, weight: 30 },
      farmingPractices: { score: farmingPracticesScore, weight: 20 },
      conservation: { score: conservationScore, weight: 10 }
    },

    // Monoculture assessment
    monocultureRisk,

    // Recommendations (minimum 3 per PRD)
    recommendations
  };
};

/**
 * Generate biodiversity recommendations based on assessment
 */
const generateBiodiversityRecommendations = (assessment) => {
  const recommendations = [];

  // Species diversity recommendations
  if (assessment.speciesDiversityScore < 60) {
    recommendations.push({
      category: 'Species Diversity',
      priority: 'High',
      action: 'Increase crop variety to minimum 5 different species',
      expectedImpact: '+15% biodiversity score',
      timeline: '1-2 seasons'
    });
  }

  // Habitat quality recommendations
  if (assessment.habitatQualityScore < 70) {
    recommendations.push({
      category: 'Habitat Quality',
      priority: 'Medium',
      action: 'Establish hedgerows and buffer strips along field boundaries',
      expectedImpact: '+10% habitat quality',
      timeline: '1 year'
    });
  }

  // Farming practices recommendations
  if (assessment.farmingPracticesScore < 70) {
    recommendations.push({
      category: 'Farming Practices',
      priority: 'Medium',
      action: 'Adopt integrated pest management (IPM) to reduce chemical inputs',
      expectedImpact: '+12% farming practices score',
      timeline: '6 months'
    });
  }

  // Conservation recommendations
  if (assessment.conservationScore < 60) {
    recommendations.push({
      category: 'Conservation',
      priority: 'Medium',
      action: 'Designate 10% of land for wildlife corridors and native species',
      expectedImpact: '+20% conservation score',
      timeline: '1-2 years'
    });
  }

  // Add monoculture recommendations if high risk
  if (assessment.monocultureRisk.riskLevel.level === 'Critical' ||
    assessment.monocultureRisk.riskLevel.level === 'High') {
    recommendations.push({
      category: 'Monoculture Risk',
      priority: 'Critical',
      action: `Reduce ${assessment.monocultureRisk.dominantCrop} concentration through crop diversification`,
      expectedImpact: '-20% monoculture risk',
      timeline: 'Immediate'
    });
  }

  // Ensure minimum 3 recommendations per PRD
  if (recommendations.length < 3) {
    recommendations.push({
      category: 'General',
      priority: 'Low',
      action: 'Continue monitoring biodiversity trends monthly',
      expectedImpact: 'Maintain current performance',
      timeline: 'Ongoing'
    });
  }

  if (recommendations.length < 3) {
    recommendations.push({
      category: 'General',
      priority: 'Low',
      action: 'Document species observations and share data with regional database',
      expectedImpact: 'Improved regional planning',
      timeline: 'Ongoing'
    });
  }

  if (recommendations.length < 3) {
    recommendations.push({
      category: 'General',
      priority: 'Low',
      action: 'Participate in pollinator habitat programs',
      expectedImpact: '+5% biodiversity score',
      timeline: '6 months'
    });
  }

  return recommendations;
};

/**
 * Track species observations
 * 
 * @param {Object} observation - Species observation data
 * @returns {Object} Processed observation with classifications
 */
export const processSpeciesObservation = (observation) => {
  const { species, count, location, observedBy, observedAt, isNative, isEndangered } = observation;

  return {
    id: `obs-${Date.now()}`,
    species,
    count,
    location,
    observedBy,
    observedAt: observedAt || new Date().toISOString(),
    classifications: {
      isNative: isNative || false,
      isEndangered: isEndangered || false,
      category: isEndangered ? 'endangered' : isNative ? 'native' : 'common'
    },
    verificationStatus: 'pending'
  };
};

/**
 * Compare biodiversity across multiple regions
 * 
 * @param {Object[]} regions - Array of region biodiversity data
 * @returns {Object} Comparison results
 */
export const compareRegionalBiodiversity = (regions) => {
  if (!regions || regions.length === 0) {
    return { regions: [], summary: null };
  }

  const assessedRegions = regions.map(region => ({
    regionId: region.id,
    regionName: region.name,
    assessment: calculateCompositeBiodiversityScore(region.data)
  }));

  // Calculate summary statistics
  const scores = assessedRegions.map(r => r.assessment.compositeScore);
  const summary = {
    averageScore: Math.round(scores.reduce((a, b) => a + b, 0) / scores.length * 10) / 10,
    highestScore: Math.max(...scores),
    lowestScore: Math.min(...scores),
    highestRegion: assessedRegions.find(r => r.assessment.compositeScore === Math.max(...scores))?.regionName,
    lowestRegion: assessedRegions.find(r => r.assessment.compositeScore === Math.min(...scores))?.regionName,
    criticalRiskCount: assessedRegions.filter(r =>
      r.assessment.monocultureRisk.riskLevel.level === 'Critical'
    ).length
  };

  return {
    regions: assessedRegions,
    summary
  };
};

const biodiversityService = {
  calculateShannonIndex,
  calculateSimpsonIndex,
  calculateSpeciesRichness,
  calculateEvenness,
  calculateMonocultureRisk,
  calculateCompositeBiodiversityScore,
  getBiodiversityRating,
  processSpeciesObservation,
  compareRegionalBiodiversity,
  MONOCULTURE_RISK_LEVELS,
  BIODIVERSITY_RATINGS
};

export default biodiversityService;
